import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FolderOpen, FileText, Eye, Calendar, BarChart2, ExternalLink } from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import { Card } from '../../components';
import { projectsService, articlesService } from '../../services';

const DashboardGrid = styled.div`
  display: grid;
  gap: 2rem;
  margin-bottom: 3rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const StatCard = styled(Card)`
  text-align: center;

  .icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 3rem;
    height: 3rem;
    background-color: ${props => props.theme.colors.primaryLight};
    border-radius: 50%;
    margin: 0 auto 1rem auto;
    color: ${props => props.theme.colors.primary};
  }

  .number {
    font-size: ${props => props.theme.typography.h2};
    font-weight: 700;
    color: ${props => props.theme.colors.text.primary};
    margin-bottom: 0.5rem;
  }

  .label {
    color: ${props => props.theme.colors.text.secondary};
    font-size: ${props => props.theme.typography.small};
    font-weight: 500;
  }
`;

const RecentSection = styled.section`
  margin-bottom: 3rem;

  h2 {
    margin-bottom: 1.5rem;
    color: ${props => props.theme.colors.text.primary};
  }
`;

const RecentGrid = styled.div`
  display: grid;
  gap: 1.5rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const RecentCard = styled(Card)`
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.md};
  }
`;

const RecentItem = styled.div`
  padding: 1.5rem;

  .title {
    font-weight: 600;
    color: ${props => props.theme.colors.text.primary};
    margin-bottom: 0.5rem;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .excerpt {
    color: ${props => props.theme.colors.text.secondary};
    font-size: ${props => props.theme.typography.small};
    margin-bottom: 1rem;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .meta {
    display: flex;
    align-items: center;
    gap: 1rem;
    color: ${props => props.theme.colors.text.tertiary};
    font-size: ${props => props.theme.typography.xs};

    .meta-item {
      display: flex;
      align-items: center;
      gap: 0.25rem;
    }
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  color: ${props => props.theme.colors.text.secondary};
`;

const WelcomeSection = styled.div`
  margin-bottom: 3rem;
  padding: 2rem;
  background: linear-gradient(135deg, ${props => props.theme.colors.primaryLight} 0%, ${props => props.theme.colors.background.secondary} 100%);
  border-radius: ${props => props.theme.borderRadius.lg};
  text-align: center;

  h1 {
    color: ${props => props.theme.colors.text.primary};
    margin-bottom: 0.5rem;
  }

  p {
    color: ${props => props.theme.colors.text.secondary};
    margin: 0;
  }
`;

export const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalProjects: 0,
    totalArticles: 0,
    publishedArticles: 0,
    totalViews: 0,
  });
  const [recentProjects, setRecentProjects] = useState([]);
  const [recentArticles, setRecentArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [projects, articles] = await Promise.all([
          projectsService.getAllProjects(),
          articlesService.getAllArticles(),
        ]);

        const publishedArticles = articles.filter(article => article.published);

        setStats({
          totalProjects: projects.length,
          totalArticles: articles.length,
          publishedArticles: publishedArticles.length,
          totalViews: Math.floor(Math.random() * 10000) + 1000, // Mock data
        });

        setRecentProjects(projects.slice(0, 4));
        setRecentArticles(articles.slice(0, 4));
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    return timestamp.toDate().toLocaleDateString();
  };

  if (loading) {
    return (
      <AdminLayout title="Dashboard">
        <LoadingContainer>
          <p>Loading dashboard...</p>
        </LoadingContainer>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Dashboard">
      <WelcomeSection>
        <h1>Welcome to your Portfolio CMS</h1>
        <p>Manage your projects, articles, and content from this dashboard.</p>
        <a
          href="https://analytics.google.com"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginTop: '1.25rem',
            padding: '0.6rem 1.25rem',
            background: '#6B8E23',
            color: '#fff',
            borderRadius: '2rem',
            textDecoration: 'none',
            fontWeight: '600',
            fontSize: '0.875rem',
          }}
        >
          <BarChart2 size={16} />
          View Analytics & Visitor Data
          <ExternalLink size={14} />
        </a>
      </WelcomeSection>

      <DashboardGrid>
        <StatCard>
          <div className="icon">
            <FolderOpen size={24} />
          </div>
          <div className="number">{stats.totalProjects}</div>
          <div className="label">Total Projects</div>
        </StatCard>

        <StatCard>
          <div className="icon">
            <FileText size={24} />
          </div>
          <div className="number">{stats.totalArticles}</div>
          <div className="label">Total Articles</div>
        </StatCard>

        <StatCard>
          <div className="icon">
            <Eye size={24} />
          </div>
          <div className="number">{stats.publishedArticles}</div>
          <div className="label">Published Articles</div>
        </StatCard>

        <StatCard>
          <div className="icon">
            <Calendar size={24} />
          </div>
          <div className="number">{stats.totalViews.toLocaleString()}</div>
          <div className="label">Total Views</div>
        </StatCard>
      </DashboardGrid>

      <RecentGrid>
        <RecentSection>
          <h2>Recent Projects</h2>
          {recentProjects.length > 0 ? (
            recentProjects.map((project) => (
              <RecentCard key={project.id} style={{ marginBottom: '1rem' }}>
                <RecentItem>
                  <div className="title">{project.title}</div>
                  <div className="excerpt">{project.description}</div>
                  <div className="meta">
                    <div className="meta-item">
                      <Calendar size={12} />
                      {formatDate(project.createdAt)}
                    </div>
                    {project.technologies && (
                      <div className="meta-item">
                        {project.technologies.slice(0, 2).join(', ')}
                        {project.technologies.length > 2 && '...'}
                      </div>
                    )}
                  </div>
                </RecentItem>
              </RecentCard>
            ))
          ) : (
            <p style={{ color: 'var(--color-text-secondary)' }}>
              No projects yet. Create your first project!
            </p>
          )}
        </RecentSection>

        <RecentSection>
          <h2>Recent Articles</h2>
          {recentArticles.length > 0 ? (
            recentArticles.map((article) => (
              <RecentCard key={article.id} style={{ marginBottom: '1rem' }}>
                <RecentItem>
                  <div className="title">{article.title}</div>
                  <div className="excerpt">{article.excerpt}</div>
                  <div className="meta">
                    <div className="meta-item">
                      <Calendar size={12} />
                      {formatDate(article.createdAt)}
                    </div>
                    <div className="meta-item">
                      {article.published ? 'Published' : 'Draft'}
                    </div>
                  </div>
                </RecentItem>
              </RecentCard>
            ))
          ) : (
            <p style={{ color: 'var(--color-text-secondary)' }}>
              No articles yet. Write your first article!
            </p>
          )}
        </RecentSection>
      </RecentGrid>
    </AdminLayout>
  );
};